import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { useOutletContext } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'

import SearchFilter from '@App/components/SearchFilter/SearchFilter'
import Dropdown from '@App/components/Dropdown/Dropdown'
import { fetchLabResultsByMission } from '@services/data.service'

import { getLabResultHoverKey, getVisibleLabResultAnalysis } from '../lab-results.slice'

import ListResultListItem from './LabResultsListItem/LabResultsListItem'

import './lab-results-list.styl'

const EMPTY_RESULTS = []

function LabResultsList() {
  const { map } = useOutletContext()
  const [searchValue, setSearchValue] = useState('')
  const [sortValue, setSortValue] = useState(null)
  const { activeProject } = useSelector(state => state.missionSetup)
  const {
    selectionRange,
    sourcesActiveNames,
    labResultActiveAnalysisTypeIds,
    labResultActiveResultTypes,
    clickedResultKey,
  } = useSelector(state => state.labResults)
  const resultsListRef = useRef(null)

  const { data: results = EMPTY_RESULTS, isLoading } = useQuery({
    queryKey: [
      'labResults', activeProject?.id, selectionRange,
      sourcesActiveNames, labResultActiveAnalysisTypeIds,
    ],
    queryFn: () => fetchLabResultsByMission(
      activeProject.id,
      sourcesActiveNames,
      undefined,
      selectionRange.startDate,
      selectionRange.endDate,
      undefined,
      labResultActiveAnalysisTypeIds,
    ),
    enabled: !!activeProject?.id,
  })

  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'date', label: 'Date' },
    { id: 'status', label: 'Status' },
  ]

  const filteredAndSortedResults = useMemo(() => {
    const filtered = results.filter((result) => {
      const matchesSearch = !searchValue
        || result?.properties?.nice_name?.toLowerCase().includes(searchValue.toLowerCase())
      const analysis = Array.isArray(result?.properties?.analysis) ? result.properties.analysis : []
      const visibleAnalysis = getVisibleLabResultAnalysis(
        analysis,
        labResultActiveAnalysisTypeIds,
        labResultActiveResultTypes,
      )

      return matchesSearch && visibleAnalysis.length > 0
    })

    return [...filtered].sort((a, b) => {
      if (sortValue?.id === 'name') {
        const nameA = a?.properties?.nice_name || ''
        const nameB = b?.properties?.nice_name || ''
        return nameA.localeCompare(nameB)
      }

      if (sortValue?.id === 'date') {
        const dateA = new Date(a?.properties?.processed || a?.properties?.record_time).getTime()
        const dateB = new Date(b?.properties?.processed || b?.properties?.record_time).getTime()
        return dateB - dateA
      }

      if (sortValue?.id === 'status') {
        const hasPositiveResult = (result) => {
          const analysis = Array.isArray(result?.properties?.analysis) ? result.properties.analysis : []
          const visibleAnalysis = getVisibleLabResultAnalysis(
            analysis,
            labResultActiveAnalysisTypeIds,
            labResultActiveResultTypes,
          )

          return visibleAnalysis.some(item => item.result?.toLowerCase() === 'positive')
        }
        return Number(hasPositiveResult(b)) - Number(hasPositiveResult(a))
      }

      return 0
    })
  }, [labResultActiveAnalysisTypeIds, labResultActiveResultTypes, results, searchValue, sortValue])

  useEffect(() => {
    if (!clickedResultKey || !resultsListRef.current) return

    const clickedIndex = filteredAndSortedResults.findIndex(
      result => getLabResultHoverKey(result) === clickedResultKey,
    )
    if (clickedIndex < 0) return

    resultsListRef.current.scrollToIndex({
      index: clickedIndex,
      align: 'center',
      behavior: 'smooth',
    })
  }, [clickedResultKey, filteredAndSortedResults])

  return (
    <div className="lab-results-list">
      <div className="lab-results-list__head">
        <div className="lab-results-list__head__filter">
          <SearchFilter value={searchValue} setValue={setSearchValue} />
        </div>
        <div className="lab-results-list__head__content">
          <Dropdown
            className="lab-results-list__head__content__sort"
            searchOptions={sortOptions}
            value={sortValue}
            setValue={setSortValue}
          />
        </div>
      </div>
      {!isLoading && filteredAndSortedResults.length > 0
        ? (
            <div className="lab-results-list__content">
              <Virtuoso
                ref={resultsListRef}
                className="lab-results-list__virtualized"
                data={filteredAndSortedResults}
                increaseViewportBy={400}
                computeItemKey={(index, result) => result.id || result.properties?.nice_name || result.properties?.record_time || index}
                itemContent={(_index, result) => (
                  <div className="lab-results-list__row">
                    <ListResultListItem
                      map={map}
                      result={result}
                      activeAnalysisTypeIds={labResultActiveAnalysisTypeIds}
                      activeResultTypes={labResultActiveResultTypes}
                    />
                  </div>
                )}
              />
            </div>
          )
        : (
            <p className="lab-results-list__no-data-message">
              {isLoading ? 'Loading lab results...' : 'No lab results found for the selected filters.'}
            </p>
          )}
    </div>
  )
}

export default LabResultsList
